import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "hooks/auth/useAuth";
import { DateTime } from "luxon";
import { createList, updateList, deleteList, archiveList, moveList } from "core/freelo/list";
import { createCard } from "core/freelo/card";
import { getBoards, createBoard, getBoard } from "core/freelo/board";
import { createLabel, updateCardLabels, deleteLabel } from "core/freelo/label";
import { uploadAttachment, deleteAttachment } from "core/freelo/attachment";
import { updateListColor, updateBoard } from "core/freelo/board";
import {
  CreateCardParams,
  UpdateCardParams,
  MoveCardParams,
  UpdateListParams,
  BoardData,
  CreateLabelParams,
  UpdateCardLabelsParams,
  DeleteLabelParams,
  MoveCardToTrashParams,
  AddCommentParams,
  UpdateCommentParams,
  AttachmentParams,
  UpdateBoardParams,
  DeleteListParams,
} from "types/freelo/crud";
import { moveCard } from "core/freelo/card/moveCard";
import { Board, Card, CardComment, List } from "@freetech/models/freelo";
import { useAdminPermissions } from "hooks/auth/useAdminPermissions";
import { addDoc, collection, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "config/firebase";
import { getDoc } from "firebase/firestore";
import { NestedBoard } from "types/freelo/nested";

const getListNameById = (board: NestedBoard | undefined, listId: string): string => {
  return board?.lists?.find(list => list.id === listId)?.name || listId;
};

export const useFreelo = () => {
  const queryClient = useQueryClient();
  const { userInfo, currentUser, role } = useAuth();
  const { checkPermission } = useAdminPermissions();
  if (!currentUser) {
    throw new Error("User not found");
  }

  const {
    data: boards,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["boards"],
    queryFn: getBoards,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const createBoardMutation = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      const boardData: BoardData = {
        name,
        order: (boards?.length || 0) + 1,
        createdBy: userInfo?.id || "system",
        lists: [],
        createdAt: DateTime.now().toISO(),
        updatedAt: DateTime.now().toISO(),
      };
      return createBoard(boardData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["boards"] });
    },
  });

  const updateListMutation = useMutation({
    mutationFn: updateList,
    onMutate: async ({ boardId, listId, updates }: UpdateListParams) => {
      await queryClient.cancelQueries({ queryKey: ["board", boardId] });
      const previousBoard = queryClient.getQueryData<NestedBoard>(["board", boardId]);

      if (previousBoard) {
        const updatedBoard = {
          ...previousBoard,
          lists: previousBoard.lists?.map(list => (list.id === listId ? { ...list, ...updates } : list)),
        };
        queryClient.setQueryData(["board", boardId], updatedBoard);
      }

      return { previousBoard };
    },
    onError: (err: Error, variables: UpdateListParams, context: { previousBoard?: NestedBoard } | undefined) => {
      if (context?.previousBoard) {
        queryClient.setQueryData(["board", variables.boardId], context.previousBoard);
      }
    },
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const deleteListMutation = useMutation({
    mutationFn: async ({ boardId, listId }: DeleteListParams) => {
      await deleteList({ boardId, listId }, queryClient);
      return { boardId, listId };
    },

    onError: (error, { boardId }) => {
      console.error("Error deleting list:", error);
    },
  });

  const archiveListMutation = useMutation({
    mutationFn: async ({ boardId, listId }: { boardId: string; listId: string }) => {
      const board = await getBoard(boardId);
      const listName = getListNameById(board, listId);

      await archiveList({ boardId, listId });
    },
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const createCardMutation = useMutation({
    mutationFn: async (params: CreateCardParams) => {
      return createCard(params, userInfo?.id || "system");
    },
    onMutate: async ({ boardId, listId, title }) => {
      await queryClient.cancelQueries({ queryKey: ["board", boardId] });
      const previousBoard = queryClient.getQueryData<NestedBoard>(["board", boardId]);

      if (previousBoard) {
        return { previousBoard };
      }
      return { previousBoard: undefined };
    },
    onError: (err: Error, variables: CreateCardParams, context: { previousBoard?: NestedBoard } | undefined) => {
      if (context?.previousBoard) {
        queryClient.setQueryData(["board", variables.boardId], context.previousBoard);
      }
    },
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const updateCardMutation = useMutation({
    mutationFn: async ({ boardId, cardId, updates }: { boardId: string; cardId: string; updates: Partial<Card> }) => {
      const cardRef = doc(db, "boards", boardId, "cards", cardId);

      const sanitizedUpdates = Object.entries(updates).reduce(
        (acc, [key, value]) => {
          acc[key] = value === undefined ? null : value;
          return acc;
        },
        {} as Record<string, any>
      );

      await updateDoc(cardRef, {
        ...sanitizedUpdates,
        updatedAt: DateTime.now().toISO(),
      });

      const updatedCardDoc = await getDoc(cardRef);
      return { id: cardId, ...updatedCardDoc.data() } as Card;
    },
    onMutate: async ({ boardId, cardId, updates }) => {
      await queryClient.cancelQueries({ queryKey: ["board", boardId] });
      const previousBoard = queryClient.getQueryData<NestedBoard>(["board", boardId]);

      if (previousBoard) {
        const updatedBoard = {
          ...previousBoard,
          cards: previousBoard.cards.map(card => (card.id === cardId ? { ...card, ...updates } : card)),
        };

        queryClient.setQueryData(["board", boardId], updatedBoard);
        return { previousBoard };
      }

      return { previousBoard: undefined };
    },
    onError: (err, { boardId }, context) => {},
    onSettled: (_, __, { boardId }) => {},
  });

  const moveCardMutation = useMutation<void, Error, MoveCardParams>({
    mutationFn: params => moveCard(params, queryClient, currentUser),
  });

  const createLabelMutation = useMutation({
    mutationFn: (params: CreateLabelParams) => createLabel(params),
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const updateCardLabelsMutation = useMutation({
    mutationFn: (params: UpdateCardLabelsParams) => updateCardLabels(params),
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const deleteLabelMutation = useMutation({
    mutationFn: (params: DeleteLabelParams) => deleteLabel(params),
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const moveCardToTrashMutation = useMutation({
    mutationFn: async (params: MoveCardToTrashParams) => {
      const { boardId, cardId, card, listId } = params;
      const cardTrashRef = collection(db, "boards", boardId, "cardTrash");

      await addDoc(cardTrashRef, {
        ...card,
        originalListId: listId,
        deletedAt: DateTime.now().toISO(),
      });

      const cardRef = doc(db, "boards", boardId, "cards", cardId);
      await deleteDoc(cardRef);
    },
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const updateListColorMutation = useMutation({
    mutationFn: ({ boardId, listId, color }: { boardId: string; listId: string; color: string | null }) =>
      updateListColor({ boardId, listId, color }),
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const updateBoardMutation = useMutation({
    mutationFn: (params: UpdateBoardParams) => updateBoard(params),
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
      queryClient.invalidateQueries({ queryKey: ["boards"] });
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: async (params: AddCommentParams) => {
      const { boardId, cardId, content, mentionedUserIds, createdBy } = params;
      const cardRef = doc(db, "boards", boardId, "cards", cardId);
      const cardDoc = await getDoc(cardRef);
      const card = cardDoc.data() as Card;

      const newComment: CardComment = {
        id: crypto.randomUUID(),
        content,
        createdAt: DateTime.now().toISO(),
        updatedAt: DateTime.now().toISO(),
        createdBy: createdBy || "system",
        mentionedUserIds,
      };

      await updateDoc(cardRef, {
        comments: [...(card.comments || []), newComment],
        updatedAt: DateTime.now().toISO(),
      });
    },
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({
      boardId,
      cardId,
      commentId,
      content,
      mentionedUserIds,
    }: {
      boardId: string;
      cardId: string;
      commentId: string;
      content: string;
      mentionedUserIds?: string[];
    }) => {
      const cardRef = doc(db, "boards", boardId, "cards", cardId);
      const cardDoc = await getDoc(cardRef);
      const card = cardDoc.data() as Card;

      if (!card.comments) {
        throw new Error("Card has no comments");
      }

      const updatedComments = card.comments.map((comment: CardComment) => {
        if (comment.id === commentId) {
          return {
            ...comment,
            content,
            updatedAt: DateTime.now().toISO(),
            mentionedUserIds,
          };
        }
        return comment;
      });

      await updateDoc(cardRef, {
        comments: updatedComments,
        updatedAt: DateTime.now().toISO(),
      });
    },
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const uploadAttachmentMutation = useMutation({
    mutationFn: (params: AttachmentParams) => uploadAttachment(params),
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: ({ boardId, cardId, attachmentId }: { boardId: string; cardId: string; attachmentId: string }) =>
      deleteAttachment({ boardId, cardId, attachmentId }),
    onSuccess: (_, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const moveListMutation = useMutation({
    mutationFn: async ({
      boardId,
      listId,
      destinationIndex,
    }: {
      boardId: string;
      listId: string;
      destinationIndex: number;
    }) => {
      await moveList({
        boardId,
        listId,
        destinationIndex,
      });
    },
    onError: error => {
      console.error("Error moving list:", error);
    },
  });

  const addListMutation = useMutation({
    mutationFn: (params: { boardId: string; name: string }) => createList(params, queryClient),
    onError: error => {
      console.error("Error adding list:", error);
    },
    onSettled: (_, __, { boardId }) => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  const getAccessibleBoards = (boards: NestedBoard[]) => {
    const isAdmin = role === "admin";
    const canViewFreelo = isAdmin ? checkPermission("freelo", "view") : true;
    const canCreateBoards = isAdmin ? checkPermission("freelo", "create") : true;
    const hasViewAllAccess = isAdmin ? checkPermission("freelo", "view", "view_all") : false;
    const hasViewMyBoardsAccess = isAdmin ? checkPermission("freelo", "view", "view_my_boards") : true;

    return boards.filter(board => {
      if (role === "admin") {
        if (!canViewFreelo) return false;

        if (hasViewAllAccess) return true;

        if (hasViewMyBoardsAccess) {
          const hasReadAccess = board.readUserIds?.includes(userInfo?.id || "");
          const hasWriteAccess = board.writeUserIds?.includes(userInfo?.id || "");
          const isCreator = board.createdBy === userInfo?.id;
          return hasReadAccess || hasWriteAccess || isCreator;
        }

        return false;
      }

      const hasReadAccess = board.readUserIds?.includes(userInfo?.id || "");
      const hasWriteAccess = board.writeUserIds?.includes(userInfo?.id || "");
      const isCreator = board.createdBy === userInfo?.id;
      return hasReadAccess || hasWriteAccess || isCreator;
    });
  };

  return {
    boards,
    isLoading,
    error,
    createBoard: createBoardMutation,
    updateBoard: updateBoardMutation,
    createList: addListMutation,
    updateList: updateListMutation,
    deleteList: deleteListMutation,
    archiveList: archiveListMutation,
    moveList: moveListMutation,
    addList: addListMutation,
    updateListColor: updateListColorMutation,
    createCard: createCardMutation,
    updateCard: updateCardMutation,
    moveCard: moveCardMutation,
    moveCardToTrash: moveCardToTrashMutation,
    createLabel: createLabelMutation,
    updateCardLabels: updateCardLabelsMutation,
    deleteLabel: deleteLabelMutation,
    addComment: addCommentMutation,
    updateComment: updateCommentMutation,
    uploadAttachment: uploadAttachmentMutation,
    deleteAttachment: deleteAttachmentMutation,
    getAccessibleBoards,
  };
};
